import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { IS_RECMAPPER } from './product';

export interface BackgroundOperation {
  id: number;
  operation_id: string;
  operation_type: string;
  job_id: number;
  project_id: number;
  job_name: string;
  project_name: string;
  status: string; // 'processing' | 'completed' | 'error'
  total_records: number;
  processed_records: number;
  added_count: number;
  updated_count: number;
  skipped_count: number;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

interface BackgroundJobsContextType {
  operations: BackgroundOperation[];
  activeCount: number;
  completedCount: number;
  hasUndismissed: boolean;
  startOperation: (op: {
    operationId: string;
    jobId: number;
    projectId: number;
    jobName: string;
    projectName: string;
    totalRecords: number;
  }) => void;
  dismissOperation: (id: number) => void;
  dismissAll: () => void;
  refreshOperations: () => void;
}

const BackgroundJobsContext = createContext<BackgroundJobsContextType | null>(null);

export const useBackgroundJobs = () => {
  const context = useContext(BackgroundJobsContext);
  if (!context) {
    throw new Error('useBackgroundJobs must be used within a BackgroundJobsProvider');
  }
  return context;
};

export const BackgroundJobsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [operations, setOperations] = useState<BackgroundOperation[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const fetchOperations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || IS_RECMAPPER) return; // no CSV enrichment jobs in RecMapper

      const response = await fetch('/api/enrichment/background-operations', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok && mountedRef.current) {
        const data = await response.json();
        setOperations(data.operations || []);
      }
    } catch (e) {
      // Ignore fetch errors silently
    }
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    mountedRef.current = true;
    fetchOperations();

    // Poll every 3 seconds while there are active operations, otherwise every 30s
    const startPolling = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => {
        fetchOperations();
      }, 3000);
    };

    startPolling();

    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchOperations]);

  // Slow down polling when no active operations
  useEffect(() => {
    const hasActive = operations.some(op => op.status === 'processing');

    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      fetchOperations();
    }, hasActive ? 3000 : 30000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [operations, fetchOperations]);

  const startOperation = useCallback((op: {
    operationId: string;
    jobId: number;
    projectId: number;
    jobName: string;
    projectName: string;
    totalRecords: number;
  }) => {
    // Add optimistically - will be confirmed on next poll
    const newOp: BackgroundOperation = {
      id: -Date.now(), // Temporary ID until DB confirms
      operation_id: op.operationId,
      operation_type: 'add_to_project',
      job_id: op.jobId,
      project_id: op.projectId,
      job_name: op.jobName,
      project_name: op.projectName,
      status: 'processing',
      total_records: op.totalRecords,
      processed_records: 0,
      added_count: 0,
      updated_count: 0,
      skipped_count: 0,
      error: null,
      created_at: new Date().toISOString(),
      completed_at: null
    };

    setOperations(prev => [newOp, ...prev]);

    // Trigger immediate refresh to get real DB record
    setTimeout(() => fetchOperations(), 1000);
  }, [fetchOperations]);

  const dismissOperation = useCallback(async (id: number) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/enrichment/background-operations/${id}/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOperations(prev => prev.filter(op => op.id !== id));
    } catch (e) {
      console.error('Failed to dismiss operation:', e);
    }
  }, []);

  const dismissAll = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/enrichment/background-operations/dismiss-all', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOperations(prev => prev.filter(op => op.status === 'processing'));
    } catch (e) {
      console.error('Failed to dismiss all operations:', e);
    }
  }, []);

  const activeCount = operations.filter(op => op.status === 'processing').length;
  const completedCount = operations.filter(op => op.status === 'completed' || op.status === 'error').length;
  const hasUndismissed = operations.length > 0;

  return (
    <BackgroundJobsContext.Provider value={{
      operations,
      activeCount,
      completedCount,
      hasUndismissed,
      startOperation,
      dismissOperation,
      dismissAll,
      refreshOperations: fetchOperations
    }}>
      {children}
    </BackgroundJobsContext.Provider>
  );
};
